import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHead, apiErr, fmtQ, toNum, useMerendaMeta } from "@/merenda/common";

const FoodDialog = ({ food, units, scopeParams, onClose, onDone }) => {
  const [f, setF] = useState(food || { name: "", unit: "kg", decimals: 3, packages: [], active: true, ref_per_service: "" });
  const save = async () => {
    const body = { ...f, decimals: Number(f.decimals), ref_per_service: toNum(f.ref_per_service), packages: f.packages.filter((p) => p.label && toNum(p.factor) > 0).map((p) => ({ label: p.label, factor: toNum(p.factor) })) };
    try {
      if (food?.id) await api.put(`/merenda/foods/${food.id}`, body, { params: scopeParams });
      else await api.post("/merenda/foods", body, { params: scopeParams });
      toast.success("Alimento salvo"); onDone();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const pk = (i, k, v) => setF({ ...f, packages: f.packages.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="food-dialog">
        <DialogHeader><DialogTitle>{food?.id ? "Editar alimento" : "Novo alimento"}</DialogTitle></DialogHeader>
        <div><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="food-name" /></div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label>Unidade</Label><select className="h-10 w-full rounded-md border px-2" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="food-unit">{units.concat(units.includes(f.unit) ? [] : [f.unit]).map((u) => <option key={u}>{u}</option>)}</select></div>
          <div><Label>Casas decimais</Label><Input type="number" min={0} max={4} value={f.decimals} onChange={(e) => setF({ ...f, decimals: e.target.value })} data-testid="food-decimals" /></div>
          <div><Label>Ref./atendimento</Label><Input inputMode="decimal" placeholder="ex.: 0,08" value={f.ref_per_service ?? ""} onChange={(e) => setF({ ...f, ref_per_service: e.target.value })} data-testid="food-ref" /></div>
        </div>
        <div><Label>Embalagens (conversão para {f.unit})</Label>
          {f.packages.map((p, i) => (
            <div key={i} className="mt-1 flex gap-2"><Input placeholder="Ex.: Pacote 5 kg" value={p.label} onChange={(e) => pk(i, "label", e.target.value)} data-testid={`food-pkg-label-${i}`} /><Input className="w-28" placeholder="Fator" value={p.factor} onChange={(e) => pk(i, "factor", e.target.value)} data-testid={`food-pkg-factor-${i}`} />
              <Button size="icon" variant="ghost" onClick={() => setF({ ...f, packages: f.packages.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button></div>
          ))}
          <Button size="sm" variant="outline" className="mt-1" onClick={() => setF({ ...f, packages: [...f.packages, { label: "", factor: "" }] })} data-testid="food-add-pkg"><Plus className="mr-1 h-4 w-4" /> Embalagem</Button>
        </div>
        <Label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="food-active" /> Ativo</Label>
        <DialogFooter><Button onClick={save} data-testid="food-save">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Alimentos() {
  const { meta, foods, reload, scopeParams } = useMerendaMeta();
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  if (!meta) return <div className="grid min-h-[40vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;
  return (
    <div data-testid="alimentos-page">
      <PageHead title="Alimentos" subtitle={`${foods.length} gêneros. Lista inicial transcrita do Controle Mensal de Estoque do modelo — confira nomes e unidades.`}>
        <Button onClick={() => setEdit({})} data-testid="food-new"><Plus className="mr-1 h-4 w-4" /> Novo alimento</Button>
      </PageHead>
      <Input className="mb-3 max-w-sm" placeholder="Buscar" value={q} onChange={(e) => setQ(e.target.value)} data-testid="food-search" />
      <Card className="overflow-x-auto border-slate-200">
        <Table>
          <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Unid.</TableHead><TableHead>Embalagens</TableHead><TableHead>Ref./atend.</TableHead><TableHead>Situação</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>{foods.filter((f) => f.name.toLowerCase().includes(q.toLowerCase())).map((f) => (
            <TableRow key={f.id} data-testid={`food-row-${f.id}`}>
              <TableCell className="font-medium">{f.name}</TableCell><TableCell>{f.unit}</TableCell>
              <TableCell className="text-xs">{f.packages.map((p) => `${p.label} = ${fmtQ(p.factor)} ${f.unit}`).join("; ") || "—"}</TableCell>
              <TableCell>{f.ref_per_service != null ? `${fmtQ(f.ref_per_service)} ${f.unit}` : "—"}</TableCell>
              <TableCell><Badge className={f.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}>{f.active ? "Ativo" : "Inativo"}</Badge></TableCell>
              <TableCell><Button size="icon" variant="ghost" onClick={() => setEdit(f)} data-testid={`food-edit-${f.id}`}><Pencil className="h-4 w-4" /></Button></TableCell>
            </TableRow>))}</TableBody>
        </Table>
      </Card>
      {edit && <FoodDialog food={edit.id ? edit : null} units={meta.units} scopeParams={scopeParams} onClose={() => setEdit(null)} onDone={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
